package twist.mena.sports.admin.screens

import androidx.compose.runtime.*
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import twist.mena.sports.admin.api.AdminApiClient
import twist.mena.sports.admin.components.Column
import twist.mena.sports.admin.components.DataTable
import twist.mena.sports.admin.components.SearchBar
import twist.mena.sports.admin.state.AuthState
import twist.mena.sports.admin.state.UiState
import twist.mena.sports.admin.theme.*
import twist.mena.sports.shared.dto.admin.AdminTeamResponse
import twist.mena.sports.shared.dto.admin.NameOverrideRequest
import twist.mena.sports.shared.dto.admin.OffsetPage

@Composable
fun TeamsScreen(apiClient: AdminApiClient) {
    val colors = AppTheme.colors
    var state by remember { mutableStateOf<UiState<OffsetPage<AdminTeamResponse>>>(UiState.Loading) }
    var search by remember { mutableStateOf("") }
    var offset by remember { mutableStateOf(0) }
    var editingTeamId by remember { mutableStateOf<String?>(null) }
    var editNameAr by remember { mutableStateOf("") }
    var editNameEn by remember { mutableStateOf("") }
    val scope = rememberCoroutineScope()
    val isEditor = AuthState.getRole()?.let {
        it in listOf("scora_editor", "scora_admin", "scora_super_admin")
    } ?: false

    fun loadTeams() {
        scope.launch {
            state = UiState.Loading
            try {
                val page = apiClient.getTeams(search.ifBlank { null }, offset)
                state = UiState.Success(page)
            } catch (e: Exception) {
                state = UiState.Error("Failed to load teams")
            }
        }
    }

    LaunchedEffect(search, offset) { loadTeams() }

    SearchBar(placeholderText = "Search teams...") { query ->
        search = query
        offset = 0
    }

    when (val s = state) {
        is UiState.Loading -> P({ style { color(Color(colors.textMuted)) } }) { Text("Loading...") }
        is UiState.Error -> P({ style { color(Color(colors.danger)) } }) { Text(s.message) }
        is UiState.Success -> {
            val page = s.data
            val columns = listOf(
                Column<AdminTeamResponse>("Arabic Name") { team ->
                    if (editingTeamId == team.id && isEditor) {
                        Div({ style { display(DisplayStyle.Flex); gap(Spacing.xs) } }) {
                            Input(type = InputType.Text) {
                                value(editNameAr)
                                onInput { editNameAr = it.value }
                                style {
                                    width(150.px)
                                    formInputStyle(colors)
                                    padding(Spacing.xs)
                                    fontSize(Typography.sm)
                                }
                            }
                            Button({
                                onClick {
                                    scope.launch {
                                        try {
                                            apiClient.updateNameOverride(
                                                team.id,
                                                NameOverrideRequest(
                                                    nameEnOverride = editNameEn.ifBlank { null },
                                                    nameArOverride = editNameAr.ifBlank { null }
                                                )
                                            )
                                            editingTeamId = null
                                            loadTeams()
                                        } catch (e: Exception) { }
                                    }
                                }
                                style {
                                    padding(Spacing.xs, Spacing.sm)
                                    primaryButtonStyle(colors)
                                    fontSize(Typography.sm)
                                }
                            }) { Text("Save") }
                            Button({
                                onClick { editingTeamId = null }
                                style {
                                    padding(Spacing.xs, Spacing.sm)
                                    secondaryButtonStyle(colors)
                                    fontSize(Typography.sm)
                                }
                            }) { Text("Cancel") }
                        }
                    } else {
                        Span({
                            if (isEditor) {
                                onClick {
                                    editingTeamId = team.id
                                    editNameAr = team.nameArOverride ?: team.nameAr ?: ""
                                    editNameEn = team.nameEnOverride ?: team.nameEn
                                }
                                style { cursor("pointer"); property("border-bottom", "1px dashed ${colors.textMuted}") }
                            }
                        }) {
                            Text(team.displayNameAr ?: team.displayNameEn)
                        }
                        if (team.nameArOverride != null) {
                            Span({
                                style { fontSize(Typography.xs); color(Color(colors.info)); marginLeft(Spacing.xs) }
                            }) { Text("(edited)") }
                        }
                    }
                },
                Column("English Name") { team -> Text(team.displayNameEn) },
                Column("Vendor ID") { team ->
                    Span({ style { fontFamily("monospace"); fontSize(Typography.sm) } }) {
                        Text(team.vendorTeamId)
                    }
                },
                Column("Last Synced") { team ->
                    Span({ style { fontSize(Typography.sm); color(Color(colors.textMuted)) } }) {
                        Text(team.syncedAtUtc.take(10))
                    }
                }
            )

            DataTable(
                columns = columns,
                rows = page.data,
                total = page.total,
                offset = page.offset,
                limit = page.limit,
                onPageChange = { newOffset -> offset = newOffset }
            )
        }
    }
}
